import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import type { OnDestroy } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { ThemeToggleComponent } from './theme-toggle.component';

interface NavItem {
  readonly label: string;
  readonly path: string;
}

interface SocialLink {
  readonly label: string;
  readonly href: string;
  readonly icon: string;
}

@Component({
  selector: 'app-site-header',
  imports: [RouterLink, RouterLinkActive, ThemeToggleComponent],
  templateUrl: './site-header.component.html',
  styleUrl: './site-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteHeaderComponent implements OnDestroy {
  readonly featureItems: readonly NavItem[] = [
    { label: 'True Availability', path: '/features/true-availability' },
    { label: 'Calendar UI', path: '/features/calendar-ui' },
    { label: 'Event Polling', path: '/features/meeting-polling' },
    { label: 'Booking Pages', path: '/features/smart-booking' },
    { label: 'Product Roadmap', path: '/roadmap' }
  ];

  readonly solutionItems: readonly NavItem[] = [
    { label: 'For Independent Consultants', path: '/solutions/independent-consultants' },
    { label: 'For Sales Teams', path: '/solutions/sales-teams' }
  ];

  readonly primaryItems: readonly NavItem[] = [
    { label: 'Pricing', path: '/pricing' },
    { label: 'About', path: '/about' }
  ];

  readonly socialLinks: readonly SocialLink[] = [
    {
      label: 'LinkedIn',
      href: 'https://www.linkedin.com/company/heyea/',
      icon: '/assets/b9ce6f119974543779bbcad7563e234be8edd900.webp'
    },
    {
      label: 'YouTube',
      href: 'https://www.youtube.com/@Heyea-ai',
      icon: '/assets/6d78522dd3ecd58c7a9bf1f59dd5244f5f51ae78.webp'
    },
    {
      label: 'Reddit',
      href: 'https://www.reddit.com/r/heyea/',
      icon: '/assets/921ec5eeae43b46d37a78774edb8ef71f25b7730.webp'
    },
    {
      label: 'X (Twitter)',
      href: 'https://x.com/HeyeaAI',
      icon: '/assets/0aa41878d0ceb77292d9f847b2f4e21d688460c1.webp'
    }
  ];

  readonly showFeaturesMenu = signal(false);
  readonly showSolutionsMenu = signal(false);
  readonly mobileMenuOpen = signal(false);
  readonly mobileFeaturesOpen = signal(false);
  readonly mobileSolutionsOpen = signal(false);

  private featuresMenuTimer: ReturnType<typeof setTimeout> | undefined;
  private solutionsMenuTimer: ReturnType<typeof setTimeout> | undefined;

  ngOnDestroy(): void {
    this.clearDesktopMenuTimers();
  }

  openFeaturesMenu(): void {
    if (this.featuresMenuTimer) {
      clearTimeout(this.featuresMenuTimer);
      this.featuresMenuTimer = undefined;
    }

    this.showFeaturesMenu.set(true);
  }

  scheduleFeaturesClose(): void {
    this.featuresMenuTimer = setTimeout(() => {
      this.showFeaturesMenu.set(false);
      this.featuresMenuTimer = undefined;
    }, 1000);
  }

  openSolutionsMenu(): void {
    if (this.solutionsMenuTimer) {
      clearTimeout(this.solutionsMenuTimer);
      this.solutionsMenuTimer = undefined;
    }

    this.showSolutionsMenu.set(true);
  }

  scheduleSolutionsClose(): void {
    this.solutionsMenuTimer = setTimeout(() => {
      this.showSolutionsMenu.set(false);
      this.solutionsMenuTimer = undefined;
    }, 1000);
  }

  toggleMobileMenu(): void {
    const next = !this.mobileMenuOpen();
    this.mobileMenuOpen.set(next);

    if (!next) {
      this.mobileFeaturesOpen.set(false);
      this.mobileSolutionsOpen.set(false);
    }
  }

  toggleMobileFeatures(): void {
    this.mobileFeaturesOpen.update((current) => !current);
  }

  toggleMobileSolutions(): void {
    this.mobileSolutionsOpen.update((current) => !current);
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
    this.mobileFeaturesOpen.set(false);
    this.mobileSolutionsOpen.set(false);
  }

  private clearDesktopMenuTimers(): void {
    if (this.featuresMenuTimer) {
      clearTimeout(this.featuresMenuTimer);
      this.featuresMenuTimer = undefined;
    }

    if (this.solutionsMenuTimer) {
      clearTimeout(this.solutionsMenuTimer);
      this.solutionsMenuTimer = undefined;
    }
  }
}
